/**
 * Resolves a master-dropdown selection back to its row's BizCode.
 *
 * Dropdown form values keep using whatever they already used (an internal
 * master id, or the display label for autocomplete-style fields) — the UI
 * and validation are unaffected. This is used only when building a step's
 * submission payload, to additionally send the BizCode the backend wants,
 * under the same key name the master API itself uses for that code.
 *
 * @param {object} apiCache - state.journey.apiCache
 * @param {string} cacheKey - apiCache key, e.g. "salutationdetails"
 * @param {string|string[]} matchFields - row field(s) compared against `value` (first match wins)
 * @param {string} bizCodeField - row field holding the BizCode
 * @param {*} value - the currently selected form value
 * @returns {string}
 */
// Trimmed + case-insensitive: label-based matches (bank name, country name,
// occupation category, salutation title, relation) come from free-text
// autocomplete input or API label text whose casing/whitespace isn't
// guaranteed to match the master row exactly. Numeric/code ids compare
// identically either way, so normalizing everything is safe.
const normalizeMatchValue = (v) => String(v ?? "").trim().toLowerCase();

/** Finds the master row matching `value` against any of `matchFields`. */
function findMasterRow(apiCache, cacheKey, matchFields, value) {
  if (value == null || value === "") return { row: null, list: [] };
  const list = apiCache?.[cacheKey]?.data || [];
  const fields = Array.isArray(matchFields) ? matchFields : [matchFields];
  const target = normalizeMatchValue(value);
  const row = list.find((item) =>
    fields.some(
      (f) => item?.[f] != null && normalizeMatchValue(item[f]) === target,
    ),
  );
  return { row, list, fields };
}

function warnIfUnresolved(cacheKey, fields, value, row, list, resolvedField) {
  if (typeof window === "undefined") return;
  if (!row && list.length > 0) {
    // Loud rather than silent: without this, a field-name mismatch between
    // what the dropdown stores and what the master actually returns just
    // shows up as an empty value in the payload with no clue why.
    // eslint-disable-next-line no-console
    console.warn(
      `[masterBizCodes] no "${cacheKey}" row matched ${JSON.stringify(fields)} = ${JSON.stringify(value)}. Sample row keys:`,
      Object.keys(list[0] || {}),
    );
  } else if (row && resolvedField && (row[resolvedField] == null || row[resolvedField] === "")) {
    // eslint-disable-next-line no-console
    console.warn(
      `[masterBizCodes] matched a "${cacheKey}" row for ${JSON.stringify(value)} but it has no "${resolvedField}" value. Row:`,
      row,
    );
  }
}

export function resolveMasterBizCode(
  apiCache,
  cacheKey,
  matchFields,
  bizCodeField,
  value,
) {
  const { row, list, fields } = findMasterRow(apiCache, cacheKey, matchFields, value);
  warnIfUnresolved(cacheKey, fields, value, row, list, bizCodeField);
  return row?.[bizCodeField] ?? "";
}

/** Resolves the row's display label (e.g. `documentName`, `countryName`). */
export function resolveMasterLabel(
  apiCache,
  cacheKey,
  matchFields,
  labelField,
  value,
) {
  const { row, list, fields } = findMasterRow(apiCache, cacheKey, matchFields, value);
  warnIfUnresolved(cacheKey, fields, value, row, list, labelField);
  return row?.[labelField] ?? "";
}

/** Resolves both the label and BizCode from a single scan of the master list. */
export function resolveMasterLabelAndBizCode(
  apiCache,
  cacheKey,
  matchFields,
  labelField,
  bizCodeField,
  value,
) {
  const { row, list, fields } = findMasterRow(apiCache, cacheKey, matchFields, value);
  warnIfUnresolved(cacheKey, fields, value, row, list, bizCodeField);
  return { label: row?.[labelField] ?? "", bizCode: row?.[bizCodeField] ?? "" };
}

/** Central map of {cacheKey, matchFields, bizCodeField} per master, keyed by
 * the form field whose value should be resolved. Reused across tabs so the
 * field/master pairing only needs correcting in one place if a master's
 * response shape changes. */
export const MASTER_BIZ_CODE_LOOKUPS = {
  salutation: {
    cacheKey: "salutationdetails",
    matchFields: ["salutationID"],
    labelField: "salutationTitle",
    bizCodeField: "salutationBizCode",
  },
  salutationByTitle: {
    cacheKey: "salutationdetails",
    matchFields: ["salutationTitle"],
    labelField: "salutationTitle",
    bizCodeField: "salutationBizCode",
  },
  relationshipByLabel: {
    cacheKey: "relationshipdetails",
    matchFields: ["relation"],
    labelField: "relation",
    bizCodeField: "relationshipBizCode",
  },
  bankByName: {
    cacheKey: "bankdetails",
    matchFields: ["bankName"],
    labelField: "bankName",
    bizCodeField: "bankBizCode",
  },
  bankByCode: {
    cacheKey: "bankdetails",
    matchFields: ["bankCode"],
    labelField: "bankName",
    bizCodeField: "bankBizCode",
  },
  countryByName: {
    cacheKey: "countrydetails",
    matchFields: ["countryName"],
    labelField: "countryName",
    bizCodeField: "countryBizCode",
  },
  companyByName: {
    cacheKey: "companydetails",
    matchFields: ["insurerCompanyName"],
    labelField: "insurerCompanyName",
    bizCodeField: "companyBizCode",
  },
  addressProofById: {
    cacheKey: "addressdocumentproof",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "addrsProofBizCode",
  },
  ageProofById: {
    cacheKey: "agedocumentproof",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "ageProofBizCode",
  },
  identificationProofById: {
    cacheKey: "identificationdocumentproof",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "identificationBizCode",
  },
  // Both OccupationCategories and OccupationType dropdowns store the
  // master row's `documentID` as their value, same casing as the other
  // id-based masters below.
  occupationCategoryById: {
    cacheKey: "OccupationCategories",
    matchFields: ["documentID"],
    labelField: "occupationCategory",
    bizCodeField: "occupationCategoryBizCode",
  },
  occupationTypeById: {
    cacheKey: "OccupationType",
    matchFields: ["documentID"],
    labelField: "occupation",
    bizCodeField: "occupationTypeBizCode",
  },
  causeOfDeathByLabel: {
    cacheKey: "causeOfDeathMaster",
    matchFields: ["deathCause"],
    labelField: "deathCause",
    bizCodeField: "deathCauseBizCode",
  },
  familyMemberByLabel: {
    cacheKey: "familyMemberRelationships",
    matchFields: ["relationship"],
    labelField: "relationship",
    bizCodeField: "familyMemberBizCode",
  },
  // The following all replace hardcoded option arrays. Their dropdown value
  // is the master row's `documentID` (not the label, and not the BizCode) —
  // label and BizCode are resolved back from that id for the payload.
  maritalStatusById: {
    cacheKey: "maritalStatus",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "maritalStatusBizCode",
  },
  residentialStatusById: {
    cacheKey: "residentialStatus",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "residentialBizCode",
  },
  existingPolicyStatusById: {
    cacheKey: "existingPolicyStatus",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "existingPolicyBizCode",
  },
  verificationMethodById: {
    cacheKey: "verificationMethod",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "verificationBizCode",
  },
  purposeOfInsuranceById: {
    cacheKey: "purposeofInsurance",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "purposeOfInsuranceBizCode",
  },
  tobaccoOptionById: {
    cacheKey: "tobaccoOption",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "tobaccoOptionBizCode",
  },
  tobaccoQuantityById: {
    cacheKey: "tobaccoQuantity",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "tobaccoQuantityBizCode",
  },
  travelPurposeById: {
    cacheKey: "travelPurpose",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "travelPurposeBizCode",
  },
  alcoholOptionById: {
    cacheKey: "alcoholOption",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "alcoholOptionBizCode",
  },
  alcoholQuantityById: {
    cacheKey: "alcoholQuantity",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "alcoholQuantityBizCode",
  },
  educationById: {
    cacheKey: "educationMaster",
    matchFields: ["documentID"],
    labelField: "educationLevel",
    bizCodeField: "educationBizCode",
  },
  incomeProofById: {
    cacheKey: "incomeproofs",
    matchFields: ["documentID"],
    labelField: "documentName",
    bizCodeField: "incomeProofBizCode",
  },
};

/** Resolves one of the named lookups above against `apiCache`. */
export function resolveBizCode(apiCache, lookupKey, value) {
  const cfg = MASTER_BIZ_CODE_LOOKUPS[lookupKey];
  if (!cfg) return "";
  return resolveMasterBizCode(
    apiCache,
    cfg.cacheKey,
    cfg.matchFields,
    cfg.bizCodeField,
    value,
  );
}

/** Resolves just the display label for one of the named lookups above. */
export function resolveLabel(apiCache, lookupKey, value) {
  const cfg = MASTER_BIZ_CODE_LOOKUPS[lookupKey];
  if (!cfg) return "";
  return resolveMasterLabel(
    apiCache,
    cfg.cacheKey,
    cfg.matchFields,
    cfg.labelField,
    value,
  );
}

/** Resolves both label and BizCode for one of the named lookups above. */
export function resolveLabelAndBizCode(apiCache, lookupKey, value) {
  const cfg = MASTER_BIZ_CODE_LOOKUPS[lookupKey];
  if (!cfg) return { label: "", bizCode: "" };
  return resolveMasterLabelAndBizCode(
    apiCache,
    cfg.cacheKey,
    cfg.matchFields,
    cfg.labelField,
    cfg.bizCodeField,
    value,
  );
}

/** True when a Liferay master product flag is Yes / true / 1. */
export function isMasterYesFlag(value) {
  if (value === true) return true;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "yes" || normalized === "true" || normalized === "1";
}

/** Builds `{label, value}` dropdown options from a master list shaped like
 * `{documentID, documentName, ...}` — value is the row's id field (default
 * `documentID`; pass `idField` for masters using a different id field name),
 * label defaults to
 * documentName (pass `labelField` for masters that use a differently-named
 * label column, e.g. educationMaster's `educationLevel`). Used for the
 * several masters that replaced hardcoded option arrays.
 *
 * Optional `yesFlagField` keeps only rows whose product flag is Yes
 * (e.g. incomeproofs filtered by `nishchitPension` for this journey). */
export function buildIdOptions(
  apiCache,
  cacheKey,
  labelField = "documentName",
  idField = "documentID",
  yesFlagField,
) {
  const rows = apiCache?.[cacheKey]?.data || [];
  const filtered = yesFlagField
    ? rows.filter((item) => isMasterYesFlag(item?.[yesFlagField]))
    : rows;
  return filtered.map((item) => ({
    label: item[labelField],
    value: item[idField],
  }));
}

/** Journey names stored in OccupationType.eligibleJourneyOption. */
const OCCUPATION_JOURNEY_ALIASES = {
  cancer: ["cancer"],
  combo: ["combo"],
  nishchit: ["nishchit", "nischit"],
  nischit: ["nishchit", "nischit"],
};

function occupationEligibilityTokens(value) {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value.flatMap((entry) =>
      occupationEligibilityTokens(
        entry && typeof entry === "object"
          ? (entry.name ?? entry.label ?? entry.key ?? "")
          : entry,
      ),
    );
  }
  if (typeof value === "object") {
    return occupationEligibilityTokens(
      value.name ?? value.label ?? value.key ?? value.en_US ?? "",
    );
  }
  return String(value)
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Occupation Type rows list allowed products in `eligibleJourneyOption`
 * (for example "Combo, Cancer, Nishchit" or "Cancer"). A row is shown only
 * when the current journey is in that list. Rows saved before the column
 * existed stay visible.
 */
export function isOccupationEligibleForJourney(row, journey) {
  if (
    !row ||
    !Object.prototype.hasOwnProperty.call(row, "eligibleJourneyOption")
  ) {
    return true;
  }
  const key = String(journey ?? "")
    .trim()
    .toLowerCase();
  const aliases = OCCUPATION_JOURNEY_ALIASES[key] || (key ? [key] : []);
  if (!aliases.length) return true;
  const tokens = occupationEligibilityTokens(row.eligibleJourneyOption);
  return tokens.some((token) => aliases.includes(token));
}

/** Occupation Type dropdown limited to one product journey. */
export function buildOccupationTypeOptions(apiCache, journey) {
  const rows = apiCache?.OccupationType?.data || [];
  return rows
    .filter((item) => isOccupationEligibleForJourney(item, journey))
    .map((item) => ({
      label: item.occupation,
      value: item.documentID,
    }));
}

/** Resolves Occupation Category's label, BizCode, and `laSOE` together —
 * the one master here with an extra field beyond the usual label/BizCode
 * pair. */
export function resolveOccupationCategoryDetails(apiCache, value) {
  const cfg = MASTER_BIZ_CODE_LOOKUPS.occupationCategoryById;
  const { row, list, fields } = findMasterRow(
    apiCache,
    cfg.cacheKey,
    cfg.matchFields,
    value,
  );
  warnIfUnresolved(cfg.cacheKey, fields, value, row, list, cfg.bizCodeField);
  return {
    label: row?.[cfg.labelField] ?? "",
    bizCode: row?.[cfg.bizCodeField] ?? "",
    laSOE: row?.laSOE ?? "",
  };
}

/** Looks up a label from a plain `{label, value}` options array (static
 * constants like MARITAL_STATUS_OPTIONS/EDUCATION_OPTIONS) — used for
 * dropdowns that aren't apiCache-driven, so their payload can still carry a
 * Label companion key alongside the existing value. */
export function resolveStaticLabel(options, value) {
  if (value == null || value === "") return "";
  const opt = (options || []).find((o) => String(o.value) === String(value));
  return opt?.label ?? "";
}
